import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cloud, KeyRound, Pencil, Plus, Server, Trash2 } from 'lucide-react';
import type {
  CwmpOptions,
  CwmpTunnel,
  Tr069Profile,
  Tr069ProfileHealth,
  Tr069ProfilesResponse,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

export function Tr069ProfilesPage() {
  const { activeOrg, session } = useSession();
  const needsOrg = session?.user.role === 'superadmin' && !activeOrg;

  return (
    <>
      <PageHeader
        title="TR-069 profiles"
        description={
          activeOrg
            ? `The ACS servers ${activeOrg.name}'s ONUs inform to.`
            : 'The ACS servers your ONUs inform to.'
        }
      />
      {needsOrg ? (
        <Card>
          <EmptyState
            icon={<KeyRound className="size-8" />}
            title="Select an organization"
            description="Profiles are org-wide — pick the organization in the top bar first."
          />
        </Card>
      ) : (
        <Tr069ProfilesCard />
      )}
    </>
  );
}

/**
 * The org's TR-069 deployments. `local` is SimpleACS's own GenieACS (seeded
 * automatically, superadmin-only to edit, never deletable); `external` profiles
 * are other operators' ACS servers an org admin adds. Passwords are write-only:
 * the server returns whether one is set, never the value.
 */
function Tr069ProfilesCard() {
  const { session, can } = useSession();
  const queryClient = useQueryClient();
  const isSuperadmin = session?.user.role === 'superadmin';
  const canManage = can('settings.manage');
  // `new` = the add-external form; a profile = editing that one; null = closed.
  const [editing, setEditing] = useState<Tr069Profile | 'new' | null>(null);

  const profilesQuery = useQuery({
    queryKey: ['tr069-profiles'],
    queryFn: () => api.get<Tr069ProfilesResponse>('/tr069-profiles'),
  });
  const cwmpOptions = profilesQuery.data?.cwmpOptions;
  const healthQuery = useQuery({
    queryKey: ['tr069-profiles', 'health'],
    queryFn: () => api.get<{ items: Tr069ProfileHealth[] }>('/tr069-profiles/health'),
    refetchInterval: 30_000,
  });
  const healthById = new Map((healthQuery.data?.items ?? []).map((h) => [h.profileId, h]));

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/tr069-profiles/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tr069-profiles'] }),
  });

  /**
   * Revealed credentials, per profile, held only for this page view.
   *
   * Not part of the profiles query on purpose. The password is left out of
   * every list response so it is not sitting in a cache, a log or a devtools
   * panel that happened to be open — it is fetched only when somebody asks for
   * it, one profile at a time, and the ask is recorded server-side.
   */
  const [revealed, setRevealed] = useState<
    Record<string, { url: string; username: string; password: string }>
  >({});

  const reveal = useMutation({
    mutationFn: (id: string) =>
      api
        .post<{ url: string; username: string; password: string }>(
          `/tr069-profiles/${encodeURIComponent(id)}/reveal`,
        )
        .then((creds) => ({ id, creds })),
    onSuccess: ({ id, creds }) => setRevealed((r) => ({ ...r, [id]: creds })),
  });

  const regenerate = useMutation({
    mutationFn: (id: string) =>
      api
        .post<{ url: string; username: string; password: string }>(
          `/tr069-profiles/${encodeURIComponent(id)}/regenerate-password`,
        )
        .then((creds) => ({ id, creds })),
    // Shown immediately, because the operator asked for a new one in order to
    // use it — making them press "show credentials" again to see what they
    // just created would be a step that exists for no reason.
    onSuccess: ({ id, creds }) => {
      setRevealed((r) => ({ ...r, [id]: creds }));
      void queryClient.invalidateQueries({ queryKey: ['tr069-profiles'] });
    },
  });

  const profiles = profilesQuery.data?.items ?? [];

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Cloud className="size-4 text-muted" />
          TR-069 profiles
          <span className="text-xs font-normal text-faint">{profiles.length}</span>
        </span>
      }
      action={
        canManage && (
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="size-3.5" />}
            onClick={() => setEditing((v) => (v === 'new' ? null : 'new'))}
          >
            Add ACS
          </Button>
        )
      }
    >
      {editing === 'new' && (
        <ProfileForm mode="new" cwmpOptions={cwmpOptions} onClose={() => setEditing(null)} />
      )}

      {profilesQuery.isLoading ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : profiles.length === 0 ? (
        <EmptyState
          icon={<Cloud className="size-8" />}
          title="No TR-069 profiles"
          description="Add another operator's ACS, or let the local GenieACS appear on first use."
        />
      ) : (
        <Table>
          <THead>
            <th className="th">Name</th>
            <th className="th">Type</th>
            <th className="th">ACS URL</th>
            <th className="th">Status</th>
            <th className="th">Inform user</th>
            <th className="th">Priority</th>
            <th className="th" />
          </THead>
          <TBody>
            {profiles.map((profile) => {
              const isLocal = profile.kind === 'local';
              const editable = canManage && (!isLocal || isSuperadmin);
              /**
               * Reading the password is NOT the same permission as editing.
               *
               * `editable` is false for an org admin on the local profile
               * because changing its ACS URL takes the fleet offline — that is
               * a superadmin's risk to take. But the inform password is the
               * credential this org's own ONUs already carry, and the person
               * typing it into a CPE is the org admin. Gating the reveal on
               * `editable` hid it from exactly the people it was built for, on
               * the only profile this fleet has.
               */
              const canReveal = canManage && profile.hasInformPassword;
              return (
                <TRow key={profile.id}>
                  <td className="td font-medium">{profile.name}</td>
                  <td className="td">
                    <Pill tone={isLocal ? 'primary' : 'neutral'}>
                      <span className="flex items-center gap-1">
                        {isLocal ? <Server className="size-3" /> : <Cloud className="size-3" />}
                        {isLocal ? 'local GenieACS' : 'external ACS'}
                      </span>
                    </Pill>
                  </td>
                  <td className="td font-mono text-xs text-muted">{profile.acsUrl}</td>
                  <td className="td">
                    {(() => {
                      const h = healthById.get(profile.id);
                      if (!h) {
                        return (
                          <span className="text-xs text-faint">
                            {healthQuery.isLoading ? 'checking…' : '—'}
                          </span>
                        );
                      }
                      return (
                        <span className="flex flex-wrap items-center gap-1">
                          <Pill state tone={h.reachable ? 'success' : 'danger'} dot>
                            {h.reachable ? 'reachable' : 'unreachable'}
                          </Pill>
                          {h.services.map((s) => (
                            <span
                              key={s.name}
                              title={`${s.url}${s.ok && s.latencyMs != null ? ` · ${s.latencyMs} ms` : ''}`}
                            >
                              <Pill state tone={s.ok ? 'success' : 'neutral'}>{s.name}</Pill>
                            </span>
                          ))}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="td text-muted">
                    {/*
                      The username is NOT shown alongside the reveal. It used to
                      be, and it was the same fact twice: a login rendered as
                      page furniture above the control that shows the login and
                      the password together, properly labelled. Only when there
                      is nothing to reveal does this column state the username
                      on its own.
                    */}
                    {!canReveal && (
                      <>
                        <span className="font-mono text-xs">{profile.informUsername || '—'}</span>
                        {profile.informUsername && (
                          <span className="ml-1.5 text-[11px] text-faint">
                            {profile.hasInformPassword ? '· pw set' : '· no pw'}
                          </span>
                        )}
                      </>
                    )}
                    {/*
                      The actual credentials, for typing into a CPE's web UI or
                      into an ONU's `tr069-mgmt … password …` line by hand.
                      Shown only when asked for, and the asking is audited.
                    */}
                    {canReveal && (
                      <div>
                        {revealed[profile.id] ? (
                          /*
                            Both halves, each labelled, because they are copied
                            as a PAIR into a CPE's form or an ONU's
                            `tr069-mgmt … username … password …` line. Showing
                            the password on its own next to a username formatted
                            as page furniture invites pasting one and retyping
                            the other from memory.
                          */
                          <div className="grid gap-1">
                            <div className="grid grid-cols-[3rem_auto] items-center gap-x-2 gap-y-1">
                              <span className="text-[11px] uppercase tracking-wide text-faint">
                                login
                              </span>
                              <code className="select-all rounded bg-surface-3 px-1.5 py-0.5 font-mono text-xs">
                                {revealed[profile.id]?.username || '—'}
                              </code>
                              <span className="text-[11px] uppercase tracking-wide text-faint">
                                pass
                              </span>
                              <code className="select-all rounded bg-surface-3 px-1.5 py-0.5 font-mono text-xs">
                                {revealed[profile.id]?.password || '—'}
                              </code>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                className="text-[11px] text-muted underline-offset-2 hover:underline"
                                onClick={() =>
                                  setRevealed(({ [profile.id]: _gone, ...rest }) => rest)
                                }
                              >
                                hide
                              </button>
                              <button
                                type="button"
                                className="text-[11px] text-muted underline-offset-2 hover:underline disabled:opacity-50"
                                disabled={regenerate.isPending}
                                onClick={() => {
                                  // Confirmed, because the cost is not on this
                                  // screen: every ONU already carrying the old
                                  // password stops matching, and nothing here
                                  // rewrites them.
                                  if (
                                    window.confirm(
                                      'Issue a new inform password? Every ONU still configured with the current one will no longer match it, and will need the new password written to them before ACS authentication is enforced.',
                                    )
                                  ) {
                                    regenerate.mutate(profile.id);
                                  }
                                }}
                              >
                                {regenerate.isPending && regenerate.variables === profile.id
                                  ? 'issuing…'
                                  : 'new password'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="text-[11px] text-muted underline-offset-2 hover:underline disabled:opacity-50"
                              disabled={reveal.isPending}
                              onClick={() => reveal.mutate(profile.id)}
                            >
                              {reveal.isPending && reveal.variables === profile.id
                                ? 'reading…'
                                : 'show credentials'}
                            </button>
                            {/*
                              A refusal has to be visible. Without this the
                              button simply did nothing when the server said no,
                              which reads as a broken button rather than as an
                              answer.
                            */}
                            {reveal.isError && reveal.variables === profile.id && (
                              <p className="mt-1 text-[11px] text-danger">
                                {reveal.error instanceof Error
                                  ? reveal.error.message
                                  : 'Could not read the password.'}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="td tabular-nums text-muted">{profile.priority}</td>
                  <td className="td">
                    <div className="flex justify-end gap-2">
                      {editable && (
                        <Button
                          size="sm"
                          icon={<Pencil className="size-3.5" />}
                          onClick={() =>
                            setEditing((v) => (v !== 'new' && v?.id === profile.id ? null : profile))
                          }
                        >
                          Edit
                        </Button>
                      )}
                      {editable && !isLocal && (
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<Trash2 className="size-3.5" />}
                          onClick={() => {
                            if (
                              confirm(
                                `Remove "${profile.name}"? ONUs pointed at it fall back to the OLT's default, and it is detached from every OLT.`,
                              )
                            ) {
                              remove.mutate(profile.id);
                            }
                          }}
                        >
                          Remove
                        </Button>
                      )}
                      {isLocal && !isSuperadmin && (
                        <span className="text-[11px] text-faint">superadmin-managed</span>
                      )}
                    </div>
                  </td>
                </TRow>
              );
            })}
          </TBody>
        </Table>
      )}

      {editing && editing !== 'new' && (
        <div className="border-t border-line">
          <ProfileForm mode={editing} cwmpOptions={cwmpOptions} onClose={() => setEditing(null)} />
        </div>
      )}
    </Card>
  );
}

/**
 * Add or edit one TR-069 profile. On edit the password field is left blank and
 * only sent when the operator types a new one — the server never hands the
 * current value back, so "unchanged" is the only safe default.
 */
function ProfileForm({
  mode,
  cwmpOptions,
  onClose,
}: {
  mode: 'new' | Tr069Profile;
  cwmpOptions: CwmpOptions | undefined;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const editing = mode === 'new' ? null : mode;
  const isLocal = editing?.kind === 'local';
  const [form, setForm] = useState({
    name: editing?.name ?? '',
    acsUrl: editing?.acsUrl ?? '',
    cwmpTunnel: (editing?.cwmpTunnel ?? (isLocal ? 'wireguard' : 'custom')) as CwmpTunnel,
    informUsername: editing?.informUsername ?? '',
    informPassword: '',
    priority: editing?.priority ?? 2,
  });
  const [error, setError] = useState<string | null>(null);

  // The tunnel picker is only meaningful for the local GenieACS — an external
  // ACS is always a third-party (custom) URL.
  const tunnelPicker = isLocal;
  const isCustom = !tunnelPicker || form.cwmpTunnel === 'custom';
  const derivedUrl = form.cwmpTunnel === 'openvpn' ? cwmpOptions?.openvpn : cwmpOptions?.wireguard;

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        informUsername: form.informUsername.trim() || null,
        priority: form.priority,
      };
      if (tunnelPicker) body.cwmpTunnel = form.cwmpTunnel;
      // A tunnel derives the URL server-side; only send one for custom.
      if (isCustom) body.acsUrl = form.acsUrl.trim();
      // Only touch the password when the operator actually typed one.
      if (form.informPassword) body.informPassword = form.informPassword;
      if (editing)
        return api.patch<Tr069Profile>(`/tr069-profiles/${encodeURIComponent(editing.id)}`, body);
      return api.post<Tr069Profile>('/tr069-profiles', { ...body, kind: 'external' });
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['tr069-profiles'] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not save the profile'),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-4 border-b border-line p-5 md:grid-cols-2">
      <Field label="Name" hint={isLocal ? 'The local GenieACS profile' : 'A label for this ACS'}>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Acme NOC ACS"
          required
          autoFocus
        />
      </Field>
      {tunnelPicker ? (
        <Field label="CWMP tunnel" hint="Which management tunnel ONUs inform over">
          <select
            value={form.cwmpTunnel}
            onChange={(e) => setForm({ ...form, cwmpTunnel: e.target.value as CwmpTunnel })}
            className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink"
          >
            <option value="wireguard">WireGuard{cwmpOptions ? ` · ${cwmpOptions.wireguard}` : ''}</option>
            <option value="openvpn">OpenVPN{cwmpOptions ? ` · ${cwmpOptions.openvpn}` : ''}</option>
            <option value="custom">Custom URL</option>
          </select>
        </Field>
      ) : null}
      {isCustom ? (
        <Field label="ACS URL" hint="Where ONUs inform (CWMP endpoint)">
          <Input
            value={form.acsUrl}
            onChange={(e) => setForm({ ...form, acsUrl: e.target.value })}
            placeholder="http://acs.example.net:7547/"
            className="font-mono"
            required
          />
        </Field>
      ) : (
        <Field label="ACS URL" hint="Derived from the tunnel — no typos to take the fleet offline">
          <div className="flex h-[38px] items-center rounded-lg border border-line bg-surface-2 px-3 font-mono text-sm text-muted">
            {derivedUrl ?? '…'}
          </div>
        </Field>
      )}
      <Field label="Inform username" hint="Basic-auth user the ONU presents">
        <Input
          value={form.informUsername}
          onChange={(e) => setForm({ ...form, informUsername: e.target.value })}
          placeholder="simpleacs"
          className="font-mono"
        />
      </Field>
      <Field
        label="Inform password"
        hint={editing?.hasInformPassword ? 'Leave blank to keep the current one' : 'Basic-auth password'}
      >
        <Input
          type="password"
          value={form.informPassword}
          onChange={(e) => setForm({ ...form, informPassword: e.target.value })}
          placeholder={editing?.hasInformPassword ? '•••••• (unchanged)' : ''}
          className="font-mono"
        />
      </Field>
      <Field label="Priority" hint="TR-069 management VLAN priority, 0–7">
        <Input
          type="number"
          min={0}
          max={7}
          value={form.priority}
          onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
        />
      </Field>
      <div className="md:col-span-2 flex gap-2">
        <Button type="submit" variant="primary" size="sm" loading={save.isPending}>
          {editing ? 'Save' : 'Create'}
        </Button>
        <Button type="button" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
      {error && (
        <div className="md:col-span-2">
          <ErrorNotice error={new Error(error)} />
        </div>
      )}
    </form>
  );
}
